/* ============================================================================
   WAKA size logic — KB-synced board recommendation (no UI, no framework).

   Exposes a single global the UI layers call:
       window.WakaSizeCalc.recommend(kg, discipline, skill)

   • kg          number | null     rider weight in kilograms
   • discipline  'wing' | 'para'
   • skill       0 | 1 | 2         winging: Beginner/Intermediate/Advanced
                                   parawinging: Learning/Proficient (skill is
                                   clamped to 0–1 for para)
   Returns:
       { state: 'empty' }                       no / invalid weight
       { state: 'over' }                        above the lineup (> ~115 kg)
       { state: 'ok', picks: ['77L','88L'],     Performance-first, Light-wind-second
         borderline: bool }                     (single-element picks = one size)

   SOURCE OF TRUTH: waka-agent/functions/src/knowledge-base.ts
     → "Board Selection by Weight" (winging) and "Sizing for Parawinging".
   See SIZING-LOGIC-FOR-DESIGN.md. If sizing changes, update ALL FIVE copies
   together (KB §8 / design-system SIZING-LOGIC.md §8): the KB, this file,
   waka-size-picker.js's fallback mirror, waka-agent/functions/src/size-calc.ts
   (the TS port), and assets/viento.js's WING_FALLBACK (chat size-flow mirror).
   ============================================================================ */
(function () {
  'use strict';

  // Winging — [Beginner, Intermediate, Advanced] by weight bracket.
  // Performance pick first, Light-wind pick second.
  var WING = {
    small: ['66L'],                                            // < 55 kg
    b0: { 0: ['77L', '88L'],  1: ['66L', '77L'],  2: ['66L'] },         // 55–65
    b1: { 0: ['88L', '99L'],  1: ['77L', '88L'],  2: ['66L', '77L'] },  // 65–75
    b2: { 0: ['99L', '111L'], 1: ['88L', '99L'],  2: ['77L', '88L'] },  // 75–85
    b3: { 0: ['99L', '111L'], 1: ['88L', '99L'],  2: ['77L', '88L'] },  // 85–95
    b4: { 0: ['111L'],        1: ['99L', '111L'], 2: ['88L', '99L'] }   // 95+
  };
  // Parawinging — [Learning, Proficient]. Sizes UP vs winging; skill clamped 0–1.
  var PARA = {
    b0: { 0: ['88L', '99L'],  1: ['77L', '88L'] },            // < 65
    b1: { 0: ['88L', '99L'],  1: ['77L', '88L'] },            // 65–75
    b2: { 0: ['99L', '111L'], 1: ['88L', '99L'] },            // 75–85
    b3: { 0: ['99L', '111L'], 1: ['99L', '111L'] },           // 85–95
    b4: { 0: ['111L'],        1: ['99L', '111L'] }            // 95+
  };

  function bracket(kg) {
    if (kg < 65) return 'b0';
    if (kg < 75) return 'b1';
    if (kg < 85) return 'b2';
    if (kg < 95) return 'b3';
    return 'b4';
  }

  function recommend(kg, discipline, skill) {
    if (kg == null || isNaN(kg) || kg < 30 || kg > 200) return { state: 'empty' };
    if (kg > 115) return { state: 'over' };

    if (discipline === 'para') {
      var ps = Math.min(skill, 1);
      return { state: 'ok', picks: PARA[bracket(kg)][ps].slice(), borderline: false };
    }

    // winging
    var cell;
    if (kg < 55) cell = WING.small;
    else cell = WING[bracket(kg)][skill];
    return {
      state: 'ok',
      picks: cell.slice(),
      // 85–95 kg intermediate winging is the KB's documented close call
      borderline: (kg >= 85 && kg < 95 && skill === 1)
    };
  }

  window.WakaSizeCalc = { recommend: recommend };
})();
